import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Grid, InputBase, InputLabel} from '@mui/material';
import {useInfiniteQuery} from '@tanstack/react-query';
import dayjs from 'dayjs';
import {generatePath, useParams} from 'react-router-dom';
import {useTranslations} from '@vidiemme/react-i18n';
import {AuthRoutes} from '../../../navigation';
import {GetEmailHistoryResponse} from '../../../store/campaign/definitions';
import {useCampaign} from '../../../store/campaign/hooks';
import {useUser} from '../../../store/user/hooks';
import {BreadcrumbsProps} from '../../atoms/Breadcrumb/interface';
import {IconSVG} from '../../atoms/IconSVG';
import {Text} from '../../atoms/Text';
import {Select} from '../../molecules/Select';
import {Table} from '../../molecules/Table';
import {Column, TableProps} from '../../molecules/Table/interfaces';
import {PageHead} from '../../organisms/PageHead';
import {useStyles} from '../SendCampaignInvitation/styles';

const CampaignEmailHistory = () => {
  const {isShowroomManager} = useUser();
  const {campaignId} = useParams();
  const {t} = useTranslations();
  const styles = useStyles();
  const {getCampaignEmailHistory} = useCampaign();

  const breadcrumbs = useMemo<BreadcrumbsProps['breadcrumbs']>(
    () => [
      isShowroomManager
        ? {
            label: t('CampaignManagement.title'),
            to: AuthRoutes.CAMPAIGN_MANAGEMENT,
          }
        : {label: t('CampaignList.title'), to: AuthRoutes.CAMPAIGN_LIST},
      {
        label: t('Calendar.title'),
        to: generatePath(AuthRoutes.CALENDAR_DETAIL, {
          campaignId: String(campaignId),
        }),
      },
      {label: t('CampaignEmailHistory.title')},
    ],
    [campaignId, isShowroomManager, t],
  );

  const [filterAction, setFilterAction] = useState<string>('ALL');
  const filterOptions = useMemo(
    () => [
      {
        value: 'ALL',
        label: t('CampaignEmailHistory.filters.action.options.all'),
      },
      {
        value: 'SUCCESS',
        label: t('CampaignEmailHistory.filters.action.options.ok'),
      },
      {
        value: 'FAILED',
        label: t('CampaignEmailHistory.filters.action.options.ko'),
      },
    ],
    [t],
  );

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);

  const [fulltextValue, setFulltextValue] = useState('');
  const [fulltextFilter, setFulltextFilter] = useState('');

  // To debounce the fulltext filter query request
  const timerFulltextDebounce = useRef<NodeJS.Timeout | null>(null);
  const handleFulltextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (timerFulltextDebounce.current) {
        clearTimeout(timerFulltextDebounce.current);
      }

      setFulltextValue(e.target.value);

      timerFulltextDebounce.current = setTimeout(() => {
        setFulltextFilter(e.target.value);
      }, 750);
    },
    [],
  );

  useEffect(() => {
    setPage(0);
  }, [filterAction, fulltextFilter, perPage]);

  const {data, fetchNextPage} = useInfiniteQuery(
    [
      'campaign-email-history',
      campaignId,
      filterAction,
      fulltextFilter,
      perPage,
    ],
    ({pageParam = 1}) =>
      getCampaignEmailHistory(campaignId!, {
        status: filterAction,
        fulltext: fulltextFilter,
        page: pageParam,
        perPage,
      }),
    {
      enabled: !!campaignId,
      getNextPageParam: ({
        data: {
          meta: {current_page, last_page},
        },
      }) => (current_page >= last_page ? undefined : current_page + 1),
    },
  );

  const normalizedCompanyEmailHistory = useMemo(() => {
    const campaignEmailHistory = (data?.pages?.[page]?.data?.data ||
      []) as GetEmailHistoryResponse[];

    return campaignEmailHistory.map(response => ({
      id: response.id,
      email: response.to,
      subject: response.subject,
      context: response.context,
      status: response.status_code === 200 ? 'SENT' : 'NOT SENT',
      date: dayjs(response.created_at).format('DD/MM/YYYY HH:mm (Z)'),
    }));
  }, [data?.pages, page]);

  const totalEmailHistory = useMemo(
    () => data?.pages?.[0]?.data?.meta?.total || 0,
    [data?.pages],
  );

  const columns: Column<any>[] = useMemo(() => {
    return [
      {
        id: 'email',
        label: t('CampaignEmailHistory.list.columns.email'),
      },
      {
        id: 'subject',
        label: t('CampaignEmailHistory.list.columns.subject'),
      },
      {
        id: 'context',
        label: t('CampaignEmailHistory.list.columns.context'),
      },
      {
        id: 'status',
        label: t('CampaignEmailHistory.list.columns.status'),
      },
      {
        id: 'date',
        label: t('CampaignEmailHistory.list.columns.date'),
      },
    ];
  }, [t]);

  const tableMaxHeight = 'calc(90dvh - (64px + 116px))';

  const rowsPerPageOptions = useMemo(() => [25, 50, 100], []);

  const onPageChange = useCallback<TableProps<any, any>['onPageChange']>(
    (_, newPage) => {
      const pageAlreadyLoaded = Boolean(data?.pages?.[newPage]);

      if (newPage > page && !pageAlreadyLoaded) {
        fetchNextPage();
      }
      setPage(newPage);
    },
    [data?.pages, fetchNextPage, page],
  );

  const onRowsPerPageChange = useCallback<
    Required<TableProps<any, any>>['onRowsPerPageChange']
  >(e => {
    setPage(0);
    setPerPage(Number(e.target.value));
  }, []);

  return (
    <Box sx={styles.container}>
      <PageHead
        breadcrumbs={breadcrumbs}
        title={t('CampaignEmailHistory.title')}
      />

      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Select
            onChange={setFilterAction}
            value={filterAction || ''}
            label={t('CampaignEmailHistory.filters.action.label')}
            options={filterOptions}
          />
        </Grid>
        <Grid item xs={4}>
          <InputLabel shrink>
            <Text variant="small" component="span">
              {t('CampaignEmailHistory.filters.fulltext.label')}
            </Text>
          </InputLabel>
          <InputBase
            autoFocus
            value={fulltextValue}
            onChange={handleFulltextChange}
            placeholder={t('CampaignEmailHistory.filters.fulltext.placeholder')}
            endAdornment={<IconSVG icon="search" size={24} />}
          />
        </Grid>
      </Grid>

      <Table
        tableMaxHeight={tableMaxHeight}
        rows={normalizedCompanyEmailHistory}
        count={totalEmailHistory}
        rowsPerPageOptions={rowsPerPageOptions}
        columns={columns}
        page={page}
        rowsPerPage={perPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Box>
  );
};

export default CampaignEmailHistory;
